import React, { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { EnrollmentBillingItem } from "@/modules/eventRegistration/features/EventWaitlist/api/getEnrollmentBilling/types";

function fmt(amount: string | number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(typeof amount === "string" ? parseFloat(amount) : amount);
}

function companyKey(item: EnrollmentBillingItem): string {
  return item.companyName?.trim() || "—";
}

type Props = {
  open: boolean;
  onClose: () => void;
  /** Unpaid, sendable enrollments for this session. */
  sendableEnrollments: EnrollmentBillingItem[];
  /** Called with the chosen enrollments when the admin continues. */
  onContinue: (selected: EnrollmentBillingItem[]) => void;
};

/**
 * Step 1 of generating a payment link from the Payment Requests tab —
 * pick which unpaid enrollments to bill. One payment request = one
 * CARA invoice = one company, so selection locks to a single company.
 * On Continue the parent opens the recipients/send modal.
 */
export function GeneratePaymentLinkModal({
  open,
  onClose,
  sendableEnrollments,
  onContinue,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open) setSelectedIds(new Set());
  }, [open]);

  const groups = useMemo(() => {
    const map = new Map<string, EnrollmentBillingItem[]>();
    sendableEnrollments.forEach((item) => {
      const key = companyKey(item);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [sendableEnrollments]);

  const selectedItems = sendableEnrollments.filter((e) =>
    selectedIds.has(e.enrollmentId),
  );

  // Lock selection to one company — one request bills one CARA invoice.
  const lockedCompany =
    selectedItems.length > 0 ? companyKey(selectedItems[0]) : null;

  const toggle = (item: EnrollmentBillingItem) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.enrollmentId)) {
        next.delete(item.enrollmentId);
      } else {
        next.add(item.enrollmentId);
      }
      return next;
    });
  };

  const total = selectedItems.reduce(
    (acc, e) => acc + (parseFloat(e.seatPrice) || 0),
    0,
  );

  return (
    <Dialog onOpenChange={(o) => (!o ? onClose() : null)} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate payment link</DialogTitle>
          <DialogDescription>
            Pick the unpaid enrollments to bill. They must all belong to the
            same company — one link is a single invoice.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 max-h-[50vh] overflow-y-auto">
          {sendableEnrollments.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No unpaid enrollments awaiting a payment link.
            </p>
          ) : (
            <div className="space-y-4">
              {groups.map(([company, items]) => {
                const companyLocked =
                  lockedCompany !== null && lockedCompany !== company;
                return (
                  <div key={company}>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {company}
                    </p>
                    <div className="rounded-md border border-gray-200 dark:border-slate-700 divide-y divide-gray-200 dark:divide-slate-700">
                      {items.map((item) => {
                        const selected = selectedIds.has(item.enrollmentId);
                        return (
                          <button
                            className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                              companyLocked
                                ? "opacity-40 cursor-not-allowed"
                                : "hover:bg-gray-50 dark:hover:bg-slate-800/50"
                            }`}
                            disabled={companyLocked}
                            key={item.enrollmentId}
                            onClick={() => toggle(item)}
                            type="button"
                          >
                            <span
                              className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                                selected
                                  ? "bg-gray-900 dark:bg-white border-transparent"
                                  : "border-gray-300 dark:border-slate-600"
                              }`}
                            >
                              {selected && (
                                <Check className="h-3 w-3 text-white dark:text-gray-900" />
                              )}
                            </span>
                            <span className="flex-1 text-sm text-gray-900 dark:text-white truncate">
                              {item.firstName} {item.lastName}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {fmt(item.seatPrice)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button
            disabled={selectedItems.length === 0}
            onClick={() => onContinue(selectedItems)}
            type="button"
          >
            Continue · {fmt(total)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
